'use client';

import { type MouseEvent, useState } from 'react';
import { useHandleError } from '@/hooks/useHandleError';
import { apiFetch } from '@/lib/api/apiClient';
import { type CodeFile, useBoardEditorFileStore } from '@/stores/useBoardEditorFileStore';
import { useBoardEditorStore } from '@/stores/useBoardEditorStore';
import { getInitialCode, getInitialXml } from '@/(routers)/boards/editor/block/blockConfig';

export type OpeningConfirm = {
    fileId: string;
    hasUnsaved: boolean;
};

export function useBoardCodeFileActions(onClose?: () => void) {
    const { handleError, clearError, errorMessage } = useHandleError();
    const { board, setCode, appendLog, flashSaved, savedFileId } = useBoardEditorStore();
    const {
        files,
        currentFileId,
        setFiles,
        addFile,
        updateFile,
        deleteFile,
        deleteFileAndSetCurrent,
        setCurrentFile,
        discardChanges,
        prepareSave,
    } = useBoardEditorFileStore();

    const [editingId, setEditingId] = useState<string | null>(null);
    const [editingName, setEditingName] = useState('');
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [openingConfirm, setOpeningConfirm] = useState<OpeningConfirm | null>(null);
    const [showCreateConfirm, setShowCreateConfirm] = useState<boolean | 'unsaved'>(false);
    const [isClosing, setIsClosing] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [openingAction, setOpeningAction] = useState<'open' | 'discard' | 'save' | null>(null); // 追加

    // ---- utils ----

    const generateTimestampId = () => {
        const now = new Date();
        return (
            now.getFullYear().toString() +
            (now.getMonth() + 1).toString().padStart(2, '0') +
            now.getDate().toString().padStart(2, '0') +
            now.getHours().toString().padStart(2, '0') +
            now.getMinutes().toString().padStart(2, '0') +
            now.getSeconds().toString().padStart(2, '0') +
            now.getMilliseconds().toString().padStart(3, '0')
        );
    };

    const buildBlockContent = (file: CodeFile): string => {
        try {
            const dom = new DOMParser().parseFromString(file.blockContent || '<xml></xml>', 'text/xml').documentElement;
            dom.setAttribute('isBlockSynced', String(file.isBlockSynced ?? true));
            dom.setAttribute('preEditBlockContent', file.preEditBlockContent ?? '');
            return new XMLSerializer().serializeToString(dom);
        } catch {
            return file.blockContent;
        }
    };

    const makeNewFile = (timestamp: string): CodeFile => ({
        id: timestamp,
        name: `filename-${timestamp}`,
        codeContent: getInitialCode(board),
        blockContent: getInitialXml(board),
        lastSavedCodeContent: '',
        lastSavedBlockContent: '',
        isDirty: true,
        isSaving: false,
        isSaved: false,
        isNewFile: true,
        lastModified: new Date().toISOString(),
        isBlockSynced: true,
        isCodeEditConfirmed: false,
        preEditBlockContent: '',
    });

    const closePanel = () => onClose?.();

    const resetPanelState = () => {
        setShowCreateConfirm(false);
        setDeletingId(null);
        setEditingId(null);
        setOpeningConfirm(null);
        clearError();
    };

    // ---- load ----

    const loadFiles = async () => {
        try {
            setIsLoading(true);
            const res = await apiFetch(`/boards/owned/code_files`, { method: 'GET' });
            const latestFiles = useBoardEditorFileStore.getState().files;
            const serverFiles: CodeFile[] = res.user_board_editor_resources.map((resource: any) => {
                const resourceId = String(resource.resource_id);
                if (!resource.last_updated_date_time) {
                    console.warn('[loadFiles] last_updated_date_time missing:', resource);
                }
                const existingFile = latestFiles.find((f) => f.id === resourceId);
                if (existingFile?.isDirty) return existingFile;
                return {
                    ...existingFile,
                    id: resourceId,
                    name: resource.name,
                    isDirty: false,
                    isSaving: false,
                    isSaved: true,
                    lastModified: resource.last_updated_date_time,
                };
            });
            const localDraftFiles = latestFiles.filter(
                (localFile) => !serverFiles.some((s) => String(s.id) === localFile.id),
            );
            setFiles([...localDraftFiles, ...serverFiles]);
        } catch (error: unknown) {
            handleError(error);
        } finally {
            setIsLoading(false);
        }
    };

    // ---- save ----

    const handleSaveClick = async (fileId: string, e: MouseEvent<HTMLButtonElement>, skipReset = false) => {
        e.stopPropagation();
        if (!skipReset) resetPanelState();
        try {
            const prepared = prepareSave(fileId);
            if (!prepared) return;
            updateFile(fileId, { isSaving: true });

            if (prepared.isNewFile) {
                const res = await apiFetch('/boards/edit/code_files', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        code_content: prepared.codeContent,
                        block_content: prepared.blockContent,
                        file_name: prepared.name,
                    }),
                });
                const resourceId = String(res.resource_id);
                updateFile(fileId, {
                    id: resourceId,
                    isNewFile: false,
                    isSaving: false,
                    isSaved: true,
                    lastSavedCodeContent: prepared.codeContent,
                    lastSavedBlockContent: prepared.blockContent,
                    lastModified: res.last_updated_date_time,
                });
                if (currentFileId === fileId) setCurrentFile(resourceId);
                appendLog(`🔅『${prepared.name}』を保存しました`);
                flashSaved(resourceId);
            } else {
                await apiFetch('/boards/edit/code_files', {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        resource_id: Number(fileId),
                        code_content: prepared.codeContent,
                        block_content: prepared.blockContent,
                        file_name: prepared.name,
                    }),
                });
                updateFile(fileId, {
                    isSaving: false,
                    isSaved: true,
                    lastSavedCodeContent: prepared.codeContent,
                    lastSavedBlockContent: prepared.blockContent,
                    lastModified: new Date().toISOString(),
                });
                appendLog(`🔅『${prepared.name}』を保存しました`);
                flashSaved(fileId);
            }
        } catch (error: unknown) {
            updateFile(fileId, { isSaving: false });
            handleError(error);
            throw error;
        }
    };

    // ---- new file ----

    const handleNewFile = () => {
        resetPanelState();
        const currentFile = files.find((f) => f.id === currentFileId);
        setShowCreateConfirm(currentFile?.isDirty ? 'unsaved' : true);
    };

    const handleCreateCancel = () => {
        clearError();
        setShowCreateConfirm(false);
    };

    const handleCreateConfirm = () => {
        setIsClosing(true);
        const newFile = makeNewFile(generateTimestampId());
        addFile(newFile);
        setCurrentFile(newFile.id);
        setCode(newFile.codeContent);
        setShowCreateConfirm(false);
        closePanel();
    };

    const handleCreateSaveAndProceed = async () => {
        if (!currentFileId) return;
        try {
            await handleSaveClick(currentFileId, { stopPropagation: () => {} } as MouseEvent<HTMLButtonElement>);
            setIsClosing(true);
            const newFile = makeNewFile(generateTimestampId());
            addFile(newFile);
            setCurrentFile(newFile.id);
            setCode(newFile.codeContent);
            setShowCreateConfirm(false);
            closePanel();
        } catch (error: unknown) {
            handleError(error);
            setIsClosing(false);
        }
    };

    const handleCreateDiscardAndProceed = () => {
        setIsClosing(true);
        if (currentFileId) {
            const currentFile = files.find((f) => f.id === currentFileId);
            if (currentFile && !currentFile.isSaved && currentFile.lastSavedCodeContent === '') {
                deleteFile(currentFileId);
            } else {
                discardChanges(currentFileId);
                if (currentFile) setCode(currentFile.lastSavedCodeContent);
            }
        }
        const newFile = makeNewFile(generateTimestampId());
        addFile(newFile);
        setCurrentFile(newFile.id);
        setCode(newFile.codeContent);
        setShowCreateConfirm(false);
        closePanel();
    };

    // ---- file open ----

    const isUntouchedNewFile = (file: CodeFile | undefined): boolean => {
        if (!file) return false;
        return (
            file.isNewFile &&
            file.lastSavedCodeContent === '' &&
            file.codeContent === getInitialCode(board) &&
            file.blockContent === getInitialXml(board)
        );
    };

    const handleFileClick = (fileId: string) => {
        if (editingId || deletingId) return;
        clearError();
        if (openingConfirm?.fileId === fileId) {
            setOpeningConfirm(null);
            return;
        }
        const currentFile = files.find((f) => f.id === currentFileId);
        setOpeningConfirm({
            fileId,
            hasUnsaved: isUntouchedNewFile(currentFile) ? false : !!currentFile?.isDirty,
        });
    };

    const handleOpenCancel = () => setOpeningConfirm(null);

    const handleOpenConfirm = async (fileId: string, action: 'open' | 'discard' | 'save' = 'open') => {
        const file = files.find((f) => f.id === fileId);
        if (!file) return;

        try {
            setOpeningAction(action);
            if (!file.isNewFile) {
                const res = await apiFetch(`/boards/owned/code_files/${fileId}`, { method: 'GET' });
                const detail = res.user_board_editor_resource;
                const targetCode = detail.code_content || '';
                const targetBlock = detail.block_content || '';
                const dom = new DOMParser().parseFromString(targetBlock, 'text/xml').documentElement;
                updateFile(fileId, {
                    codeContent: targetCode,
                    blockContent: targetBlock,
                    lastSavedCodeContent: targetCode,
                    lastSavedBlockContent: targetBlock,
                    isDirty: false,
                    isSaved: true,
                    isBlockSynced: dom.getAttribute('isBlockSynced') !== 'false',
                    preEditBlockContent: dom.getAttribute('preEditBlockContent') ?? '',
                });
                setCode(targetCode);
            }

            // 未編集の新規ファイルは削除と切り替えを同時に行い描画の瞬断を防ぐ
            const currentFile = useBoardEditorFileStore.getState().files.find((f) => f.id === currentFileId);
            if (currentFileId && isUntouchedNewFile(currentFile)) {
                deleteFileAndSetCurrent(currentFileId, fileId);
            } else {
                setCurrentFile(fileId);
            }

            setOpeningConfirm(null);
            appendLog(`📂『${file.name}』を開きました。`);
            closePanel();
        } catch (error: unknown) {
            handleError(error);
        } finally {
            setOpeningAction(null); // 追加
        }
    };

    const handleOpenSaveAndProceed = async () => {
        if (!openingConfirm || !currentFileId) return;
        try {
            setOpeningAction('save');
            await handleSaveClick(currentFileId, { stopPropagation: () => {} } as MouseEvent<HTMLButtonElement>, true);
            handleOpenConfirm(openingConfirm.fileId, 'save');
        } catch (error: unknown) {
            handleError(error);
            setOpeningAction(null);
        }
    };

    const handleOpenDiscardAndProceed = async () => {
        if (!openingConfirm) return;
        if (currentFileId) {
            const currentFile = files.find((f) => f.id === currentFileId);
            if (currentFile && !currentFile.isSaved && currentFile.lastSavedCodeContent === '') {
                deleteFile(currentFileId);
            } else {
                discardChanges(currentFileId);
            }
        }
        setIsClosing(false);
        handleOpenConfirm(openingConfirm.fileId, 'discard');
    };

    // ---- rename ----

    const handleRenameClick = (file: CodeFile, e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        resetPanelState();
        setEditingId(file.id);
        setEditingName(file.name);
    };

    const handleRenameConfirm = async (fileId: string) => {
        clearError();
        const trimmed = editingName.trim();
        if (!trimmed) return;
        try {
            const file = files.find((f) => f.id === fileId);
            if (file && !file.isNewFile) {
                await apiFetch('/boards/edit/code_files', {
                    method: 'put',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        resource_id: fileId,
                        code_content: file.codeContent,
                        block_content: buildBlockContent(file),
                        file_name: trimmed,
                    }),
                });
                updateFile(fileId, { name: trimmed, blockContent: buildBlockContent(file) });
            } else {
                updateFile(fileId, { name: trimmed });
            }
            setEditingId(null);
            setEditingName('');
        } catch (error: unknown) {
            handleError(error);
        }
    };

    const handleRenameCancel = () => {
        setEditingId(null);
        setEditingName('');
        clearError();
    };

    // ---- delete ----

    const handleDeleteClick = (fileId: string, e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        resetPanelState();
        setDeletingId(fileId);
    };

    const handleDeleteConfirm = async (fileId: string) => {
        clearError();
        try {
            const file = files.find((f) => f.id === fileId);
            if (file && !file.isNewFile) {
                await apiFetch(`/boards/edit/code_files/${fileId}`, {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' },
                });
            }
            deleteFile(fileId);
            setDeletingId(null);
            if (currentFileId === fileId) setCode('');
        } catch (error: unknown) {
            handleError(error);
        }
    };

    const handleDeleteCancel = () => {
        setDeletingId(null);
        clearError();
    };

    // ---- current file status ----

    const currentFile = currentFileId && !isClosing ? files.find((f) => f.id === currentFileId) : undefined;
    const isCurrentDirty = !!currentFile?.isDirty;
    const isCurrentSaving = !!currentFile?.isSaving;
    const isCurrentSavedVisual =
        !isCurrentDirty && !isCurrentSaving && !!currentFileId && savedFileId === currentFileId;
    const isCurrentSaved = !isCurrentDirty && !isCurrentSaving && !isCurrentSavedVisual && !!currentFile?.isSaved;

    return {
        // state
        files,
        currentFileId,
        currentFile,
        editingId,
        setEditingId,
        editingName,
        setEditingName,
        deletingId,
        openingConfirm,
        showCreateConfirm,
        isLoading,
        isOpeningFile: !!openingAction,
        openingAction,
        errorMessage,
        isCurrentDirty,
        isCurrentSaving,
        isCurrentSavedVisual,
        isCurrentSaved,
        // handlers
        loadFiles,
        handleNewFile,
        handleCreateCancel,
        handleCreateConfirm,
        handleCreateSaveAndProceed,
        handleCreateDiscardAndProceed,
        handleFileClick,
        handleOpenCancel,
        handleOpenConfirm,
        handleOpenSaveAndProceed,
        handleOpenDiscardAndProceed,
        handleRenameClick,
        handleRenameConfirm,
        handleRenameCancel,
        handleSaveClick,
        handleDeleteClick,
        handleDeleteConfirm,
        handleDeleteCancel,
        clearError,
    };
}
